import { useCallback, useEffect, useState } from 'react';
import { ApiError, get } from './api';
import { Modal } from './ui';
import type { Location } from './api';

/**
 * Sign-in activity (owner 10/26), admins only: how and when someone signed in (code texted because they asked, or
 * given by a manager), every later sign-in and on what kind of device, wrong PINs or codes, sign-outs, texts yes/no,
 * PIN resets and access changes. Never PINs, codes or IP addresses.
 */
export interface ActivityItem { at: string; when: string; date: string; userId: number | null; employeeId: number | null; person: string; store: string; what: string; by: string | null; device: string | null; problem: boolean }

function Rows({ items, showPerson, onPerson }: { items: ActivityItem[]; showPerson: boolean; onPerson?: (i: ActivityItem) => void }) {
  return (
    <table className="tbl" style={{ width: '100%' }}>
      <thead><tr><th style={{ whiteSpace: 'nowrap' }}>When</th>{showPerson && <><th>Person</th><th>Home store</th></>}<th>What happened</th></tr></thead>
      <tbody>{items.map((i, n) => (
        <tr key={n}>
          <td style={{ whiteSpace: 'nowrap', color: '#4A5561' }}>{i.when}</td>
          {showPerson && <><td style={{ whiteSpace: 'nowrap' }}>{onPerson ? <button className="linkbtn" onClick={() => onPerson(i)}>{i.person}</button> : i.person}</td><td style={{ color: '#4A5561' }}>{i.store}</td></>}
          <td style={i.problem ? { color: '#B42318', fontWeight: 600 } : undefined}>{i.what}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

/** One person's whole history (the Activity button on Managers & access and the Employees page). */
export function ActivityModal({ employeeId, userId, onClose }: { employeeId?: number | null; userId?: number | null; onClose: () => void }) {
  const [d, setD] = useState<{ name: string; store: string; items: ActivityItem[] } | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { void (async () => {
    try { setD(await get(`/api/admin/activity/person?${employeeId ? `employee=${employeeId}` : `user=${userId}`}`)); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Can’t load the activity.'); }
  })(); }, [employeeId, userId]);
  return (
    <Modal title={d ? `${d.name} · sign-in activity` : 'Sign-in activity'} onClose={onClose} wide>
      {err && <div className="errbar">{err}</div>}
      {d && <div style={{ fontSize: 13, color: '#4A5561' }}>Newest first. Every sign-in is listed from 10/26/2026; before that, only codes, first sign-ins, texts and PIN resets were kept. PINs and codes are never shown.</div>}
      {d && !d.items.length && <div className="stnone">Nothing yet: they haven’t been given a code or signed in.</div>}
      {d && d.items.length > 0 && <div style={{ maxHeight: '60vh', overflowY: 'auto' }}><Rows items={d.items} showPerson={false} /></div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="tbtn" onClick={onClose}>Close</button></div>
    </Modal>
  );
}

/** Everyone's activity for a store (or all stores), on Managers & access. */
export function ActivityList({ locations }: { locations: Location[] }) {
  const [loc, setLoc] = useState<number | 'all'>('all');
  const [days, setDays] = useState(7);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [q, setQ] = useState('');
  const [d, setD] = useState<{ items: ActivityItem[]; more: boolean } | null>(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<ActivityItem | null>(null);
  const load = useCallback(async () => {
    try { setD(await get(`/api/admin/activity?days=${days}${loc === 'all' ? '' : `&location=${loc}`}`)); setErr(''); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Can’t load the activity.'); }
  }, [days, loc]);
  useEffect(() => { void load(); }, [load]);
  const words = q.trim().toLowerCase();
  const items = (d?.items ?? []).filter((i) => (!onlyProblems || i.problem) && (!words || i.person.toLowerCase().includes(words)));
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}><h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Sign-in activity</h2></div>
      <div style={{ fontSize: 13, color: '#4A5561' }}>Who signed in, how and when: sign-in codes (texted because they asked, or given by a manager), first sign-ins, every sign-in and on what kind of device, wrong PINs and codes (in red), texts on or off, PIN resets and access changes. Click a name for that person’s whole history. Only admins see this.</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', fontSize: 14 }}>
        <label className="acclbl">Store
          <select className="sel-sm" value={loc} onChange={(e) => setLoc(e.target.value === 'all' ? 'all' : Number(e.target.value))} aria-label="Which store's activity">
            <option value="all">All stores</option>
            {locations.filter((l) => !l.demo).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        <label className="acclbl">Last
          <select className="sel-sm" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="How many days">
            <option value={1}>24 hours</option><option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
          </select>
        </label>
        <input className="inp" style={{ maxWidth: 220 }} placeholder="Find a name" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a name" />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} /> Only wrong PINs and codes</label>
      </div>
      {err && <div className="errbar">{err}</div>}
      {d && !items.length && <div className="stnone">Nothing {onlyProblems ? 'went wrong' : 'happened'} in this time{words ? ' for that name' : ''}.</div>}
      {d && items.length > 0 && (
        <div style={{ border: '1px solid #C9CEC6', borderRadius: 8, background: '#FFFFFF', maxHeight: 520, overflowY: 'auto' }}>
          <Rows items={items} showPerson onPerson={setOpen} />
        </div>
      )}
      {d?.more && <div style={{ fontSize: 12.5, color: '#4A5561' }}>Showing the newest 500. Pick a store or fewer days to see the rest.</div>}
      {open && <ActivityModal employeeId={open.employeeId} userId={open.userId} onClose={() => setOpen(null)} />}
    </>
  );
}
